import { useState } from "react";
import "@/App.css";
import { useForm, ValidationError } from '@formspree/react';
import { 
  Droplets, 
  Wrench, 
  Flame, 
  Sparkles, 
  Home, 
  Bath, 
  Phone, 
  MapPin, 
  Clock, 
  Star, 
  Shield, 
  ChevronRight,
  Menu,
  X,
  Send,
  CheckCircle2,
  Snowflake,
  ExternalLink,
  Tag,
  Award,
  Camera,
  Trash2,
  Filter,
  CircleDot,
  Images,
  ChevronLeft
} from "lucide-react";

// Logo URL - New combined Plumbing & Air Conditioning logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/s8qinv3g_32BD25B2-.jpg";

// Promo images
const PROMO_IMAGES = {
  summerSavings: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/z7x3re5y_EA67DF28-1CD7-4765-BB1B-34CB63323096.jpeg",
  ac: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/3s1n8zkn_1000033418.jpg",
  garage: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/bvi2e42p_1000033417.jpg",
  waterHeater: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/r0swxd9o_1000033419.jpg"
};

// Trust/Work images
const TRUST_IMAGES = {
  waterHeaterInstall: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/7z15yva1_ChatGPT%20Image%20May%204%2C%202026%2C%2012_59_43%20PM.png",
  bathroomRemodel: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/pz4xxdy0_ChatGPT%20Image%20May%204%2C%202026%2C%2012_57_00%20PM.png"
};

// Rinnai images
const RINNAI_IMAGES = {
  tankless: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/nu6grg33_SENSEI%20RX.covered.garage.1%20%281%29.jpg",
  badge50Years: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/t9e4h5oa_image002.png",
  commercialHeater: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/npcu98j9_REHP50BM.png"
};

// Company/Fleet images
const COMPANY_IMAGES = {
  desertVan: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/t844wdwb_20240319_135156.jpg"
};

// Gallery images organized by category
const GALLERY_IMAGES = {
  waterHeaters: [
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/29i2ce3q_20210309_170441.jpg",
      title: "Dual Tankless Water Heater Installation",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/w9vdh65o_20240712_155025.jpg",
      title: "Triple Tankless Install - In Progress",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/z6asywuf_20240712_190209.jpg",
      title: "Triple Tankless Water Heaters - Complete",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/bhb3awj0_Screenshot_20250721_193125_Gallery.jpg",
      title: "Tankless Water Heater with Expansion Tank",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/ovvhtone_20250410_152739.jpg",
      title: "Ruud Professional Tank Water Heater",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/e6le3svd_IMG_20251030_182056.jpg",
      title: "Rinnai Tankless - Arizona Pride",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/998oei3c_20250918_172741.jpg",
      title: "Rinnai Tankless with EnergyGuide",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/h7ika1yd_20250805_001904.jpg",
      title: "Commercial Triple Rinnai Installation",
      category: "Water Heaters"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/ee7gpuji_Screenshot_20250721_193151_Gallery.jpg",
      title: "Dual Tankless with Copper Manifold",
      category: "Water Heaters"
    }
  ],
  waterSofteners: [
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/oaz8w7z8_20250709_155857.jpg",
      title: "Water Softener with Copper Manifold",
      category: "Water Softeners"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/pk3lkoju_20250113_122808.jpg",
      title: "Complete Water Treatment System",
      category: "Water Softeners"
    }
  ],
  pipeWork: [
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/0ndg0svx_20230214_144142.jpg",
      title: "Sewer Line Installation",
      category: "Pipe & Line Work"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/g2oznaln_20250303_104242.jpg",
      title: "New Construction Rough Plumbing",
      category: "Pipe & Line Work"
    }
  ],
  hvac: [
    {
      src: "https://customer-assets.emergentagent.com/job_fluid-pro-1/artifacts/4bv6rg10_IMG_4458.jpg",
      title: "HVAC Ceiling Unit Installation",
      category: "HVAC/AC"
    }
  ]
};

// Helper function to scroll to contact
const scrollToContact = () => {
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
};

// Navigation Component
const Navigation = ({ onOpenGallery }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-50/95 backdrop-blur-md border-b border-amber-100" data-testid="navigation">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-24">
          {/* Logo - Enlarged */}
          <div className="flex items-center">
            <img 
              src={LOGO_URL} 
              alt="DeModica Plumbing & Air Conditioning - Residential & Commercial" 
              className="h-20 md:h-24 w-auto"
            />
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-5">
            <button onClick={() => scrollToSection('services')} className="font-manrope text-stone-700 hover:text-terracotta-600 transition-colors" data-testid="nav-services">Services</button>
            <button onClick={() => scrollToSection('promotions')} className="font-manrope text-stone-700 hover:text-terracotta-600 transition-colors" data-testid="nav-promotions">Deals</button>
            <button onClick={onOpenGallery} className="font-manrope text-stone-700 hover:text-terracotta-600 transition-colors" data-testid="nav-gallery">Gallery</button>
            <button onClick={() => scrollToSection('about')} className="font-manrope text-stone-700 hover:text-terracotta-600 transition-colors" data-testid="nav-about">About</button>
            <button onClick={() => scrollToSection('reviews')} className="font-manrope text-stone-700 hover:text-terracotta-600 transition-colors" data-testid="nav-reviews">Reviews</button>
            {/* AC Link - Made more prominent */}
            <a 
              href="https://demodicaac.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-sky-100 text-sky-700 hover:bg-sky-200 px-4 py-2 rounded-full font-manrope font-bold transition-all"
              data-testid="nav-ac-link"
            >
              <Snowflake className="w-5 h-5" /> AC Services
            </a>
            <button onClick={() => scrollToSection('contact')} className="bg-terracotta-600 hover:bg-terracotta-700 text-white font-manrope font-bold rounded-full px-6 py-3 shadow-lg shadow-terracotta-600/20 transition-all hover:scale-105 active:scale-95" data-testid="nav-contact-btn">
              Get Free Estimate
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-stone-800" /> : <Menu className="w-6 h-6 text-stone-800" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-stone-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('services')} className="font-manrope text-stone-700 py-2 text-left">Services</button>
              <button onClick={() => scrollToSection('promotions')} className="font-manrope text-stone-700 py-2 text-left">Deals</button>
              <button onClick={() => { onOpenGallery(); setMobileMenuOpen(false); }} className="font-manrope text-stone-700 py-2 text-left">Gallery</button>
              <button onClick={() => scrollToSection('about')} className="font-manrope text-stone-700 py-2 text-left">About</button>
              <button onClick={() => scrollToSection('reviews')} className="font-manrope text-stone-700 py-2 text-left">Reviews</button>
              <a 
                href="https://demodicaac.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-3 rounded-full font-manrope font-bold w-fit"
              >
                <Snowflake className="w-5 h-5" /> AC Services <ExternalLink className="w-4 h-4" />
              </a>
              <a href="tel:480-703-5244" className="flex items-center gap-2 font-manrope text-stone-700 py-2">
                <Phone className="w-4 h-4" /> 480-703-5244
              </a>
              <button onClick={() => scrollToSection('contact')} className="bg-terracotta-600 text-white font-manrope font-bold rounded-full px-6 py-3 text-center mt-2">
                Get Free Estimate
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="pt-32 pb-16 md:pt-36 md:pb-24 bg-gradient-to-br from-stone-100 via-amber-50 to-stone-100 overflow-hidden" data-testid="hero-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="order-2 lg:order-1">
            <p className="font-manrope text-sage-700 font-bold uppercase tracking-widest text-sm mb-4">Scottsdale's Desert Plumbing Experts</p>
            <h1 className="font-barlow text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase text-stone-800 leading-[0.95] mb-6">
              QUALITY<br />PLUMBING<br />SINCE 2005.
            </h1>
            <p className="font-manrope text-stone-600 text-lg md:text-xl leading-relaxed mb-8 max-w-lg">
              DeModica Plumbing has delivered exceptional service to Scottsdale homeowners for over 2 decades. From repairs to full remodels—we keep your home flowing.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button 
                onClick={scrollToContact}
                className="bg-terracotta-600 hover:bg-terracotta-700 text-white font-manrope font-bold rounded-full px-8 py-4 shadow-lg shadow-terracotta-600/20 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                data-testid="hero-cta-btn"
              >
                Get Free Estimate <ChevronRight className="w-5 h-5" />
              </button>
              <a 
                href="tel:480-703-5244"
                className="bg-white text-stone-800 border-2 border-stone-300 hover:border-terracotta-600 hover:text-terracotta-600 font-manrope font-bold rounded-full px-8 py-4 transition-all flex items-center justify-center gap-2"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" /> 480-703-5244
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-stone-600">
                <Shield className="w-5 h-5 text-sage-600" />
                <span className="font-manrope text-sm font-semibold">Licensed ROC 222571</span>
              </div>
              <div className="flex items-center gap-2 text-stone-600">
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <span className="font-manrope text-sm font-semibold">5-Star Rated</span>
              </div>
              <img 
                src={RINNAI_IMAGES.badge50Years} 
                alt="Rinnai - 50 Years Creating a healthier way of living" 
                className="h-10 w-auto"
                data-testid="rinnai-badge-hero"
              />
            </div>
          </div>

          {/* Right - Hero Logo - Enlarged */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative w-full max-w-xl mx-auto">
              <img 
                src={LOGO_URL}
                alt="DeModica Plumbing & Air Conditioning - Residential & Commercial"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* AC Sister Company Banner - Made more prominent */}
        <div className="mt-12 bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg shadow-sky-500/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/20 flex items-center justify-center">
              <Snowflake className="w-6 h-6 md:w-7 md:h-7 text-white" />
            </div>
            <div className="text-center md:text-left">
              <p className="font-barlow text-xl md:text-2xl font-bold text-white">Need Air Conditioning Services?</p>
              <p className="font-manrope text-sky-100 text-sm md:text-base">Check out our sister company DeModica AC</p>
            </div>
          </div>
          <a 
            href="https://demodicaac.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white text-sky-700 font-manrope font-bold rounded-full px-8 py-4 hover:bg-sky-50 transition-all flex items-center gap-2 text-base md:text-lg shadow-lg"
            data-testid="ac-banner-btn"
          >
            Visit DeModica AC <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Our Work Gallery Section
const OurWorkSection = () => {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="our-work-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Camera className="w-5 h-5 text-terracotta-600" />
          <p className="font-manrope text-stone-600 font-semibold text-sm uppercase tracking-wider">Our Work in the Scottsdale Desert</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-2xl shadow-lg group">
            <img 
              src={TRUST_IMAGES.waterHeaterInstall} 
              alt="Professional water heater installation with desert mountains in background" 
              className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-6">
              <div>
                <p className="font-barlow text-xl font-bold text-white">Water Heater Installation</p>
                <p className="font-manrope text-stone-200 text-sm">Professional service in Scottsdale</p>
              </div>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl shadow-lg group">
            <img 
              src={TRUST_IMAGES.bathroomRemodel} 
              alt="Luxury bathroom remodel with desert landscape view" 
              className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-6">
              <div>
                <p className="font-barlow text-xl font-bold text-white">Bathroom Remodel</p>
                <p className="font-manrope text-stone-200 text-sm">Luxury fixtures with desert views</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Promotions Section
const PromotionsSection = () => {
  return (
    <section id="promotions" className="py-16 md:py-24 bg-stone-50" data-testid="promotions-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-terracotta-100 text-terracotta-700 px-4 py-2 rounded-full mb-4">
            <Tag className="w-4 h-4" />
            <span className="font-manrope font-bold text-sm">Special Offers</span>
          </div>
          <h2 className="font-barlow text-4xl md:text-5xl font-bold tracking-tight text-stone-800">CURRENT DEALS.</h2>
          <p className="font-manrope text-stone-600 text-lg mt-4 max-w-2xl mx-auto">
            Take advantage of our limited-time promotions on plumbing and HVAC services.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Summer Savings Promo */}
          <button 
            onClick={scrollToContact}
            className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-left"
            data-testid="promo-summer-savings"
          >
            <img 
              src={PROMO_IMAGES.summerSavings} 
              alt="Huge Summer Savings Event - Use Code HEAT for 40% Off All Services" 
              className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
              <span className="text-white font-manrope font-bold">Click to Learn More</span>
            </div>
          </button>

          {/* Water Heater Promo */}
          <button 
            onClick={scrollToContact}
            className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-left"
            data-testid="promo-water-heater"
          >
            <img 
              src={PROMO_IMAGES.waterHeater} 
              alt="Tankless Water Heater - $100/mo Interest Free" 
              className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
              <span className="text-white font-manrope font-bold">Click to Learn More</span>
            </div>
          </button>

          {/* AC Promo */}
          <a 
            href="https://demodicaac.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
            data-testid="promo-ac"
          >
            <img 
              src={PROMO_IMAGES.ac} 
              alt="AC System - $200/mo Interest Free" 
              className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
              <span className="text-white font-manrope font-bold">Visit DeModica AC</span>
            </div>
          </a>

          {/* Garage Cooling Promo */}
          <a 
            href="https://demodicaac.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
            data-testid="promo-garage"
          >
            <img 
              src={PROMO_IMAGES.garage} 
              alt="Garage Cooling - $89/mo Interest Free" 
              className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
              <span className="text-white font-manrope font-bold">Visit DeModica AC</span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

// Rinnai Featured Section
const RinnaiFeaturedSection = () => {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="rinnai-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-terracotta-600" />
              <p className="font-manrope text-terracotta-600 font-bold uppercase tracking-wider text-sm">Certified Rinnai Partner</p>
            </div>
            <h2 className="font-barlow text-3xl md:text-4xl font-bold tracking-tight text-stone-800 mb-4">
              TANKLESS WATER HEATERS
            </h2>
            <p className="font-manrope text-stone-600 text-lg leading-relaxed mb-6">
              As a certified Rinnai installer, we provide expert installation of tankless water heaters that deliver endless hot water while saving space and energy. Perfect for Arizona homes.
            </p>
            <ul className="space-y-3 mb-6">
              {['Endless hot water on demand', 'Energy efficient - save on utility bills', 'Space-saving wall-mounted design', 'Professional installation & service'].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-sage-600 flex-shrink-0" />
                  <span className="font-manrope text-stone-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-4">
              <img 
                src={RINNAI_IMAGES.badge50Years} 
                alt="Rinnai 50 Years" 
                className="h-12 w-auto"
              />
              <p className="font-manrope text-stone-500 text-sm">Trusted brand for 50 years</p>
            </div>
          </div>
          
          <div className="order-1 md:order-2">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={RINNAI_IMAGES.tankless} 
                alt="Rinnai tankless water heater installed in garage" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section - Updated with more services and click-to-contact
const services = [
  {
    icon: Droplets,
    title: "Water Heater Install",
    description: "Expert installation of tankless and traditional water heaters. Rinnai certified.",
    hasRinnai: true
  },
  {
    icon: Filter,
    title: "Water Softeners & Filtration",
    description: "Whole home filtration, water softeners, and salt-free systems for cleaner water.",
    hasRinnai: false
  },
  {
    icon: Sparkles,
    title: "Reverse Osmosis",
    description: "Crystal clear drinking water with professional filtration systems.",
    hasRinnai: false
  },
  {
    icon: CircleDot,
    title: "Toilet Repairs & Installation",
    description: "Expert toilet repair and installation services for all makes and models.",
    hasRinnai: false
  },
  {
    icon: Droplets,
    title: "Faucet Repairs & Installation",
    description: "Fix drips, leaks, and install new faucets. We handle all sorts of drips and leaks!",
    hasRinnai: false
  },
  {
    icon: Trash2,
    title: "Garbage Disposal",
    description: "Professional garbage disposal replacement and installation.",
    hasRinnai: false
  },
  {
    icon: Wrench,
    title: "Slab Leak Repair",
    description: "Advanced detection and repair of hidden slab leaks with minimal disruption.",
    hasRinnai: false
  },
  {
    icon: Bath,
    title: "Bathroom Remodel",
    description: "Complete bathroom transformations from fixtures to full renovations.",
    hasRinnai: false
  },
  {
    icon: Home,
    title: "Kitchen Remodel",
    description: "Modern kitchen plumbing for sinks, dishwashers, and appliances.",
    hasRinnai: false
  },
  {
    icon: Flame,
    title: "Gas Line Services",
    description: "Safe gas line installation and repair by certified professionals.",
    hasRinnai: false
  }
];

const ServicesSection = () => {
  return (
    <section id="services" className="py-16 md:py-24 bg-stone-50" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-12">
          <p className="font-manrope text-sage-700 font-bold uppercase tracking-widest text-sm mb-2">What We Do</p>
          <h2 className="font-barlow text-4xl md:text-5xl font-bold tracking-tight text-stone-800">OUR SERVICES.</h2>
          <p className="font-manrope text-stone-600 text-lg mt-4 max-w-2xl mx-auto">
            From emergency repairs to complete remodels, we handle all your plumbing needs with expertise and care.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {services.map((service, index) => (
            <button 
              key={index}
              onClick={scrollToContact}
              className="group relative overflow-hidden bg-white p-6 rounded-2xl border border-stone-200 shadow-lg hover:shadow-xl hover:border-terracotta-200 transition-all duration-300 text-left"
              data-testid={`service-card-${index}`}
            >
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-terracotta-500 to-terracotta-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  {service.hasRinnai && (
                    <img 
                      src={RINNAI_IMAGES.badge50Years} 
                      alt="Rinnai Certified" 
                      className="h-7 w-auto opacity-80"
                    />
                  )}
                </div>
                <h3 className="font-barlow text-lg md:text-xl font-bold text-stone-800 mb-2">{service.title}</h3>
                <p className="font-manrope text-stone-600 text-sm">{service.description}</p>
                <p className="font-manrope text-terracotta-600 text-sm font-semibold mt-3 group-hover:underline">Get a Quote →</p>
              </div>
            </button>
          ))}
        </div>

        {/* Additional Services */}
        <div className="mt-10 text-center">
          <p className="font-manrope text-stone-500 mb-4">Also offering:</p>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {['Laundry Rooms', 'Drain Cleaning', 'Pipe Repair', 'Water Line Repair'].map((item, i) => (
              <span key={i} className="bg-white text-stone-700 px-3 md:px-4 py-2 rounded-full font-manrope text-sm border border-stone-200">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section - Updated with 20+ years and fleet image
const AboutSection = () => {
  return (
    <section id="about" className="py-16 md:py-24 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800" data-testid="about-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="font-manrope text-terracotta-400 font-bold uppercase tracking-widest text-sm mb-2">About Us</p>
            <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
              SERVING SCOTTSDALE<br />FOR 20+ YEARS.
            </h2>
            <p className="font-manrope text-stone-300 text-base md:text-lg leading-relaxed mb-6">
              DeModica Plumbing has provided quality services to Scottsdale clients for over 2 decades. We combine our use of the highest quality parts and equipment with our dedication to delivering exceptional work.
            </p>
            <p className="font-manrope text-stone-300 text-base md:text-lg leading-relaxed mb-8">
              Customer satisfaction is guaranteed. We treat every home as if it were our own, providing professional care that exceeds expectations.
            </p>
            
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 md:p-6">
                <p className="font-barlow text-3xl md:text-4xl font-bold text-terracotta-400">20+</p>
                <p className="font-manrope text-stone-400 text-sm">Years Experience</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 md:p-6">
                <p className="font-barlow text-3xl md:text-4xl font-bold text-terracotta-400">100%</p>
                <p className="font-manrope text-stone-400 text-sm">Satisfaction</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Desert Van Image */}
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={COMPANY_IMAGES.desertVan}
                alt="DeModica Plumbing service van in the Scottsdale desert"
                className="w-full h-auto object-cover"
              />
            </div>
            
            {/* Why Choose Us Card */}
            <div className="bg-gradient-to-br from-terracotta-600 to-terracotta-700 rounded-2xl p-6 md:p-8">
              <h3 className="font-barlow text-xl md:text-2xl font-bold text-white mb-4">Why Choose Us?</h3>
              <ul className="space-y-2 md:space-y-3">
                {[
                  'Licensed & Insured (ROC 222571)',
                  'Highest Quality Parts & Equipment',
                  'Exceptional Customer Service',
                  'Free Estimates Available',
                  'Residential & Commercial'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-terracotta-200 flex-shrink-0 mt-0.5" />
                    <span className="font-manrope text-white text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section with HouseCallPro Widget - Mobile Optimized
const ReviewsSection = () => {
  return (
    <section id="reviews" className="py-12 md:py-24 bg-amber-50" data-testid="reviews-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-8 md:mb-12">
          <p className="font-manrope text-sage-700 font-bold uppercase tracking-widest text-sm mb-2">Testimonials</p>
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-stone-800">WHAT CLIENTS SAY.</h2>
          <p className="font-manrope text-stone-600 text-base md:text-lg mt-4 max-w-2xl mx-auto">
            See what our satisfied customers have to say about their experience with DeModica Plumbing.
          </p>
        </div>

        {/* HouseCallPro Reviews Widget - Mobile Scrollable */}
        <div 
          className="bg-white rounded-2xl shadow-lg"
          data-testid="housecallpro-widget"
        >
          <div 
            className="overflow-y-auto overflow-x-hidden rounded-2xl"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              maxHeight: '70vh'
            }}
          >
            <iframe 
              src="https://client.housecallpro.com/reviews/widget/1b9a5043-a343-43d1-9a80-77392a3ec772" 
              title="DeModica Plumbing Reviews"
              className="border-0 w-full"
              style={{ 
                height: '800px',
                minHeight: '600px'
              }}
              loading="lazy"
            />
          </div>
        </div>
        
        {/* Mobile hint */}
        <p className="text-center text-stone-400 text-xs mt-3 md:hidden">
          Swipe up within the reviews to see more
        </p>
      </div>
    </section>
  );
};

// Contact Section with FormSpree - Updated address
const ContactSection = () => {
  const [state, handleSubmit] = useForm("xvzlokdr");

  return (
    <section id="contact" className="py-16 md:py-24 bg-white" data-testid="contact-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="font-manrope text-sage-700 font-bold uppercase tracking-widest text-sm mb-2">Contact Us</p>
            <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-stone-800 mb-6">
              REQUEST<br />SERVICE NOW.
            </h2>
            <p className="font-manrope text-stone-600 text-base md:text-lg mb-8">
              Ready to get your plumbing project started? Fill out the form and we'll get back to you with a free estimate.
            </p>

            <div className="space-y-4 md:space-y-6">
              <a href="tel:480-703-5244" className="flex items-start gap-4 group">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-terracotta-100 flex items-center justify-center flex-shrink-0 group-hover:bg-terracotta-200 transition-colors">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-terracotta-600" />
                </div>
                <div>
                  <p className="font-manrope font-bold text-stone-800">Call or Text</p>
                  <p className="font-manrope text-terracotta-600 font-bold text-base md:text-lg">480-703-5244</p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 md:w-6 md:h-6 text-sage-700" />
                </div>
                <div>
                  <p className="font-manrope font-bold text-stone-800">Location</p>
                  <p className="font-manrope text-stone-600 text-sm md:text-base">7701 E Gray Rd #103, Scottsdale, AZ</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-amber-700" />
                </div>
                <div>
                  <p className="font-manrope font-bold text-stone-800">Business Hours</p>
                  <p className="font-manrope text-stone-600 text-sm md:text-base">Monday - Saturday: 8:00 AM - 6:00 PM</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 md:w-6 md:h-6 text-stone-700" />
                </div>
                <div>
                  <p className="font-manrope font-bold text-stone-800">Licensed & Insured</p>
                  <p className="font-manrope text-stone-600 text-sm md:text-base">Arizona ROC License #222571</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            {state.succeeded ? (
              <div className="bg-sage-50 border border-sage-200 rounded-2xl p-6 md:p-8 text-center" data-testid="form-success">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 md:w-8 md:h-8 text-sage-700" />
                </div>
                <h3 className="font-barlow text-xl md:text-2xl font-bold text-stone-800 mb-2">Request Submitted!</h3>
                <p className="font-manrope text-stone-600 text-sm md:text-base">Thank you for contacting us. We'll get back to you soon with your free estimate.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-stone-50 rounded-2xl p-6 md:p-8 border border-stone-200" data-testid="contact-form">
                <h3 className="font-barlow text-xl md:text-2xl font-bold text-stone-800 mb-6">Submit your request for service now</h3>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block font-manrope text-sm font-semibold text-stone-700 mb-2">Name</label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      className="w-full bg-white border border-stone-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400 outline-none transition-all text-base"
                      placeholder="Your name"
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block font-manrope text-sm font-semibold text-stone-700 mb-2">Email *</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      className="w-full bg-white border border-stone-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400 outline-none transition-all text-base"
                      placeholder="your@email.com"
                      data-testid="input-email"
                    />
                    <ValidationError prefix="Email" field="email" errors={state.errors} className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block font-manrope text-sm font-semibold text-stone-700 mb-2">Phone</label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      className="w-full bg-white border border-stone-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400 outline-none transition-all text-base"
                      placeholder="(480) 555-1234"
                      data-testid="input-phone"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="address" className="block font-manrope text-sm font-semibold text-stone-700 mb-2">Address (Street, City, Zip Code)</label>
                    <input
                      id="address"
                      type="text"
                      name="address"
                      className="w-full bg-white border border-stone-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400 outline-none transition-all text-base"
                      placeholder="123 Main St, Scottsdale, AZ 85260"
                      data-testid="input-address"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block font-manrope text-sm font-semibold text-stone-700 mb-2">How can we help?</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      className="w-full bg-white border border-stone-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400 outline-none transition-all resize-none text-base"
                      placeholder="Describe your plumbing needs..."
                      data-testid="input-message"
                    />
                    <ValidationError prefix="Message" field="message" errors={state.errors} className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={state.submitting}
                    className="w-full bg-terracotta-600 hover:bg-terracotta-700 disabled:bg-terracotta-400 text-white font-manrope font-bold rounded-full px-8 py-4 shadow-lg shadow-terracotta-600/20 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                    data-testid="submit-btn"
                  >
                    {state.submitting ? 'Sending...' : 'Send'} <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section - Hidden by default, accessible via footer link
const GallerySection = ({ isOpen, onClose }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Combine all gallery images
  const allImages = [
    ...GALLERY_IMAGES.waterHeaters,
    ...GALLERY_IMAGES.waterSofteners,
    ...GALLERY_IMAGES.pipeWork,
    ...GALLERY_IMAGES.hvac
  ];
  
  const categories = ['All', 'Water Heaters', 'Water Softeners', 'Pipe & Line Work', 'HVAC/AC'];
  const [activeCategory, setActiveCategory] = useState('All');
  
  const filteredImages = activeCategory === 'All' 
    ? allImages 
    : allImages.filter(img => img.category === activeCategory);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto" data-testid="gallery-modal">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-barlow text-3xl md:text-4xl font-bold text-white">OUR WORK</h2>
            <p className="font-manrope text-stone-400 text-sm mt-1">Photos from completed projects</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="gallery-close-btn"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
        
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                activeCategory === cat 
                  ? 'bg-terracotta-600 text-white' 
                  : 'bg-white/10 text-stone-300 hover:bg-white/20'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        
        {/* Image Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredImages.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedImage(image)}
              className="relative group overflow-hidden rounded-xl aspect-square bg-stone-800"
            >
              <img 
                src={image.src} 
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <div>
                  <p className="font-manrope text-white font-semibold text-sm">{image.title}</p>
                  <p className="font-manrope text-stone-300 text-xs">{image.category}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {filteredImages.length === 0 && (
          <p className="text-center text-stone-400 font-manrope py-12">No images in this category yet.</p>
        )}
        
        {/* Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-60 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button 
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-8 left-0 right-0 text-center">
              <p className="font-manrope text-white font-semibold">{selectedImage.title}</p>
              <p className="font-manrope text-stone-400 text-sm">{selectedImage.category}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Footer - Updated address with gallery link
const Footer = ({ onOpenGallery }) => {
  return (
    <footer className="bg-stone-900 py-12 md:py-16" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Logo & About */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="DeModica Plumbing & Air Conditioning" 
              className="h-20 md:h-24 w-auto mb-4"
            />
            <p className="font-manrope text-stone-400 text-sm max-w-md">
              Scottsdale's trusted plumbing professionals for over 20 years. Quality work, guaranteed satisfaction. Residential and commercial services.
            </p>
            <a 
              href="tel:480-703-5244"
              className="inline-flex items-center gap-2 mt-4 text-terracotta-400 font-manrope font-bold text-lg hover:text-terracotta-300 transition-colors"
            >
              <Phone className="w-5 h-5" /> 480-703-5244
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-barlow font-bold text-white text-lg mb-4">Services</h4>
            <ul className="space-y-2">
              {['Water Heater', 'Water Softeners', 'Reverse Osmosis', 'Toilet Repairs', 'Faucet Repairs', 'Garbage Disposal', 'Slab Leak Repair', 'Bathroom Remodel', 'Kitchen Remodel', 'Gas Lines'].map((item, i) => (
                <li key={i}>
                  <button 
                    onClick={scrollToContact}
                    className="font-manrope text-stone-400 text-sm hover:text-terracotta-400 transition-colors"
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-barlow font-bold text-white text-lg mb-4">Contact</h4>
            <div className="space-y-3">
              <p className="font-manrope text-stone-400 text-sm flex items-start gap-2">
                <MapPin className="w-4 h-4 text-terracotta-400 flex-shrink-0 mt-0.5" />
                7701 E Gray Rd #103, Scottsdale, AZ
              </p>
              <p className="font-manrope text-stone-400 text-sm flex items-start gap-2">
                <Clock className="w-4 h-4 text-terracotta-400 flex-shrink-0 mt-0.5" />
                Mon - Sat: 8:00 AM - 6:00 PM
              </p>
              <a 
                href="https://demodicaac.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-manrope text-sky-400 text-sm flex items-start gap-2 hover:text-sky-300 transition-colors"
              >
                <Snowflake className="w-4 h-4 flex-shrink-0 mt-0.5" />
                DeModica AC Services
              </a>
              {/* Subtle Gallery Link */}
              <button 
                onClick={onOpenGallery}
                className="font-manrope text-stone-500 text-sm flex items-start gap-2 hover:text-stone-300 transition-colors"
                data-testid="footer-gallery-link"
              >
                <Images className="w-4 h-4 flex-shrink-0 mt-0.5" />
                View Our Work
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-manrope text-stone-500 text-sm text-center md:text-left">
            © 2026 DeModica Plumbing & Air Conditioning. All Rights Reserved.
          </p>
          <div className="flex items-center gap-4">
            <img 
              src={RINNAI_IMAGES.badge50Years} 
              alt="Rinnai 50 Years" 
              className="h-8 w-auto opacity-70"
            />
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-terracotta-400" />
              <span className="font-manrope text-stone-400 text-sm">AZ Licensed ROC #222571</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [galleryOpen, setGalleryOpen] = useState(false);

  return (
    <div className="App">
      <Navigation onOpenGallery={() => setGalleryOpen(true)} />
      <HeroSection />
      <OurWorkSection />
      <PromotionsSection />
      <RinnaiFeaturedSection />
      <ServicesSection />
      <AboutSection />
      <ReviewsSection />
      <ContactSection />
      <Footer onOpenGallery={() => setGalleryOpen(true)} />
      <GallerySection isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} />
    </div>
  );
}

export default App;
